import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip, OverlayTrigger, Button, Form, Spinner } from 'react-bootstrap';
import { RiBrushAiFill } from 'react-icons/ri';
import {
  ArrowUpRightCircle,
  CheckCircleFill,
  Files,
  MenuButton,
  PlusCircle,
  InfoCircleFill,
  Pen,
  Trash,
  X
} from 'react-bootstrap-icons';
import {
  ArrowLeft,
  Building2,
  Calendar,
  FileText,
  MapPin,
  User,
  Check,
  Pencil,
  Plus,
  SquareArrowOutUpRight,
  Play,
  AlertTriangle,
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../helpers/api';
import {
  ADMIN_ROLE,
  ENDPOINTS,
  PROCESSOR_ROLE,
  cancellationReasonOptions,
  getClientFacingOrderStatus,
  servicesKeyValue
} from '../../../helpers/constants';
import {
  copyToClipboard,
  getDateFromInputString,
  getFormattedDate,
  getFullAddress,
  isAdmin,
  isClient,
  isQualiaOrganization,
  isStaff
} from '../../../helpers/global';
import { toSafeApiErrorHtml } from '../../../helpers/safeHtml';
import { splitQualiaTaskError, toastOrderUpdateResult } from '../../../helpers/qualia';
import { getInvoiceAmountForOrder } from '../../../helpers/collectedFees';
import useAppChoices from '../../../hooks/useAppChoices';
import useCancellationRequest from '../../../hooks/useCancellationRequest';
import useClosingChangeRequest from '../../../hooks/useClosingChangeRequest';
import AlertModal from '../../AlertModal';
import Notes from '../../Notes';
import OrdersTabPills from '../../OrdersTabPills';
import OrderVersionPills from './OrderVersionPills';
import UpdateHistory from './UpdateHistory';
import DriveFileReader from '../../common/drive-file-reader';
import DriveFileUploader from '../../common/drive-file-uploader';
import AddEditOrderSidebar from './AddEditOrderSidebar';
import AddEditTaskModal from './Tasks/AddEditTaskModal';
import RevisionRequestModal from './RevisionRequestModal';
import ETAReasonModal from './ETAReasonModal';
import ETARequiredModal from './ETARequiredModal';
import FinancialCloseoutModal from './FinancialCloseoutModal';
import Tasks from './Tasks/Tasks';
import Estoppel from './estoppel';
import Survey from './survey';
import LienSearch from './lien-search';
import OrderNotesSection from './OrderNotesSection';
import CancelRequestModal from './overview/modals/CancellationRequestModal';
import ClosingChangeRequestModal from './overview/modals/ClosingChangeRequestModal';
import RequestUpdateModal from './overview/modals/RequestUpdateModal';
import useRequestUpdate from '../../../hooks/useRequestUpdate';
import ActivitySidebar from './activity/ActivitySidebar';
import OrderCancellationConfirmationModal from './overview/modals/OrderCancellationConfirmationModal';
import PaymentReminderModal from './overview/modals/PaymentReminderModal';
import UndoCancellationModal from './overview/modals/UndoCancellationModal';
import { AddOrganization, getOrganizationTooltipText, getPublicServiceTooltipText } from './ServiceDetails';
import AddOrganizationForm from './addOrganization/AddOrganizationForm';
import { useHistory, useParams } from 'react-router-dom';
import useTextClamp from '../../../hooks/useTextClamp';
import ClientAttachmentsBar from './ClientAttachmentsBar';
import useMunicipalityCounty from '../../../hooks/useMunicipalityCounty';
import AdvancedSelect from '../../common/AdvancedSelect';
import InlineEditSaveCancel from './InlineEditSaveCancel';
import WorkflowModal from './WorkflowModal';
import { getPastDueUnpaidInvoices } from '../../../helpers/paymentReminderInvoices';

const FINANCIAL_CLOSEOUT_STATUSES = ['Paid', 'Pending To Pay'];
const LIEN_SEARCH_REPORT_DRIVE_PREFIX = 'Lien_Search_Report';
const LIEN_SEARCH_DRIVE_FOLDER_NAME = 'Lien Search';
const PROPERTY_APPRAISER_FILE_COUNTIES = new Set([
  'miami dade',
  'lee',
  'broward',
  'palm beach'
]);
const WORKFLOW_PANEL_HEIGHT = 'calc(100vh - 90px)';
const WORKFLOW_PANEL_STICKY_TOP = '0.5rem';

const normalizeCountyKey = county =>
  (county || '')
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isPropertyAppraiserFileCounty = county =>
  PROPERTY_APPRAISER_FILE_COUNTIES.has(normalizeCountyKey(county));

const isPropertyAppraiserDriveFile = file =>
  /Property\s*Appraiser/i.test(String(file?.fileName || file?.name || ''));

const getDriveFileViewUrl = fileId =>
  fileId ? `https://drive.google.com/file/d/${fileId}/view` : null;

/** Prefer Lien Search folder, then fall back to a recursive search. */
function findPropertyAppraiserFile(node) {
  if (!node) return null;

  const lienSearchFolder = (node.folders || []).find(
    folder => String(folder.folderName || folder.name || '') === LIEN_SEARCH_DRIVE_FOLDER_NAME
  );
  const fromLienSearch = (lienSearchFolder?.files || []).find(isPropertyAppraiserDriveFile);
  if (fromLienSearch) return fromLienSearch;

  const fromRoot = (node.files || []).find(isPropertyAppraiserDriveFile);
  if (fromRoot) return fromRoot;

  for (const sub of node.folders || []) {
    const found = findPropertyAppraiserFile(sub);
    if (found) return found;
  }
  return null;
}

function driveFolderTreeHasFileStartingWith(node, prefix) {
  if (!node) return false;
  const p = prefix.toLowerCase();
  const matches = f =>
    String(f.fileName || f.name || '')
      .toLowerCase()
      .startsWith(p);
  if ((node.files || []).some(matches)) return true;
  for (const sub of node.folders || []) {
    if ((sub.files || []).some(matches)) return true;
  }
  return false;
}

const ServiceCard = ({
  service,
  onServiceUpdate,
  onServiceDeleteClick,
  onServiceEditClick,
  onShowFilterSidebarChange,
  onServiceSortOptionChange,
  onOrderTabChange,
  serviceKeyValWithOrderType
}) => {
  const history = useHistory();
  const { id: serviceId } = useParams();

  const role = useMemo(() => localStorage.getItem('user-role'), []);

  const defaultTab = useMemo(() => {
    const orderType = service?.orders?.[0]?.orderType;
    return servicesKeyValue.find(v => v?.name === orderType) || servicesKeyValue[1];
  }, [service]);

  const [activeOrderTab, setActiveOrderTab] = useState(serviceKeyValWithOrderType || defaultTab);
  const [updatingService, setUpdatingService] = useState(false);
  const [pinningOrderId, setPinningOrderId] = useState(null);

  const attachmentOrderType = useMemo(() => {
    if (activeOrderTab?.key === 'all') {
      return service?.orders?.[0]?.orderType;
    }
    return activeOrderTab?.name;
  }, [activeOrderTab, service?.orders]);

  const handleSupportingDocumentsChange = supportingDocuments => {
    onServiceUpdate?.({ ...service, supportingDocuments });
  };

  useEffect(() => {
    if (serviceKeyValWithOrderType?.key != null) {
      setActiveOrderTab(serviceKeyValWithOrderType);
    }
  }, [serviceKeyValWithOrderType]);

  useEffect(() => {
    if (activeOrderTab?.name !== 'Lien Search') {
      setShowWorkflowPanel(false);
    }
  }, [activeOrderTab?.name]);
  const [alertModalMeta, setAlertModalMeta] = useState(null);
  const cancellation = useCancellationRequest({
    onSuccess: ({ orderId, cancellationRequested = true } = {}) => {
      const index = service.orders.findIndex(
        o => o._id === orderId || o.orderType === activeOrderTab?.name
      );
      if (index !== -1) {
        service.orders[index] = { ...service.orders[index], cancellationRequested };
        onServiceUpdate({ ...service });
      }
    },
    onSubmitStart: () => setUpdatingService(true),
    onSubmitEnd: () => setUpdatingService(false)
  });

  const closingChange = useClosingChangeRequest({
    onSuccess: response => onServiceUpdate({ ...service, ...response }),
    onSubmitStart: () => setUpdatingService(true),
    onSubmitEnd: () => setUpdatingService(false),
    alwaysCloseOnSubmitEnd: true
  });
  const [deleteOrderMeta, setDeleteOrderMeta] = useState(null);
  const [orderCancellationConfirmationModalMeta, setOrderCancellationConfirmationModalMeta] = useState(null);
  const [undoCancellationOrder, setUndoCancellationOrder] = useState(null);
  const [showUndoCancellationModal, setShowUndoCancellationModal] = useState(false);
  const [paymentReminderModalMeta, setPaymentReminderModalMeta] = useState(null);
  const [deleteFileOrderMeta, setDeleteFileOrderMeta] = useState(null);
  const [lienMuniEditOpen, setLienMuniEditOpen] = useState(false);
  const [lienMuniDraft, setLienMuniDraft] = useState('');
  const [lienMuniRowHover, setLienMuniRowHover] = useState(false);
  const [lienMuniSaving, setLienMuniSaving] = useState(false);
  const [showWorkflowPanel, setShowWorkflowPanel] = useState(false);

  const canViewActivity = isAdmin(role) || role === PROCESSOR_ROLE;

  const { municipalityOptions, ensureRecordsLoaded } = useMunicipalityCounty({ isEnabled: isStaff(role) });

  const [noteText, setNoteText] = useState('');

  const [showActivitySidebar, setShowActivitySidebar] = useState(false);
  const [addUpdateOrderMeta, setAddUpdateOrderMeta] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [addUpdateTaskModalMeta, setAddUpdateTasksModalMeta] = useState(null);
  const [revisionRequestModalMeta, setRevisionRequestModalMeta] = useState(null);
  const [taskDeleteModalMeta, setTaskDeleteModalMeta] = useState(null);
  const [currentUserInfo] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [itemActionMeta, setItemActionMeta] = useState(null);

  // Card expansion state
  const [isLegalDescriptionExpanded, setIsLegalDescriptionExpanded] = useState(false);
  const [isSpecialInstructionsExpanded, setIsSpecialInstructionsExpanded] = useState(false);

  // Refs for checking if text is clamped
  const legalDescriptionRef = React.useRef(null);
  const specialInstructionsRef = React.useRef(null);

  // Use custom hook to detect text clamping
  const isLegalDescriptionClamped = useTextClamp(
    legalDescriptionRef,
    isLegalDescriptionExpanded,
    service['legalDescription']
  );
  const isSpecialInstructionsClamped = useTextClamp(
    specialInstructionsRef,
    isSpecialInstructionsExpanded,
    service['modificationAndSpecialInstructions']
  );

  const [showETARequiredModal, setShowETARequiredModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [isSubmittingETARequired, setIsSubmittingETARequired] = useState(false);
  const [showETAReasonModal, setShowETAReasonModal] = useState(false);
  const [pendingEtaDate, setPendingEtaDate] = useState(null);

  const [showFinancialCloseoutModal, setShowFinancialCloseoutModal] = useState(false);
  const [isSubmittingFinancialCloseout, setIsSubmittingFinancialCloseout] = useState(false);
  const [pendingEtaPayload, setPendingEtaPayload] = useState(null);
  const [orderNotesRefreshKey, setOrderNotesRefreshKey] = useState(0);
  // Survives updateOrder failure after a successful invoice PATCH so notes refresh once on final success.
  const financialCloseoutNotesCreatedRef = useRef(false);

  const orgFromOptions = useAppChoices('organizations');
  const [orgs, setOrgs] = useState(orgFromOptions);
  const [newOrg, setNewOrg] = useState(null);

  // Drive files state
  const [driveFiles, setDriveFiles] = useState(null);
  const [driveFilesLoading, setDriveFilesLoading] = useState(false);
  const [propertyAppraiserOpening, setPropertyAppraiserOpening] = useState(false);
  const [verifyingLienSearchDriveForPendingToPay, setVerifyingLienSearchDriveForPendingToPay] = useState(false);
  const [loadingMetaData, setLoadingMetaData] = useState({
    quickBooksExpenses: false,
    eta: false,
    status: false
  });

  // inline editing state for service detail fields
  const [inlineEdit, setInlineEdit] = useState({ field: null, draft: '', saving: false });

  const addEditOrderEditsFromService = useMemo(() => {
    if (!addUpdateOrderMeta?._id) return addUpdateOrderMeta;
    const fresh = service?.orders?.find(o => o._id === addUpdateOrderMeta._id);
    if (!fresh) return addUpdateOrderMeta;
    return { ...addUpdateOrderMeta, ...fresh };
  }, [addUpdateOrderMeta, service?.orders]);

  const servicesTabs = useMemo(
    () =>
      servicesKeyValue
        .filter(kv => kv.key !== 'all')
        .map(kv => {
          const hasOrder = !!service.orders.find(v => v.orderType === kv?.name);
          return hasOrder
            ? {
              ...kv,
              hasOrder: true,
              name: (
                <div className="d-flex align-items-center">
                  <Check className="icon-md mr-1" /> {kv.name}
                </div>
              )
            }
            : {
              ...kv,
              hasOrder: false,
              name: (
                <div className="d-flex align-items-center">
                  <Plus className="icon-md mr-1" /> Add {kv.name}
                </div>
              )
            };
        }),
    [service]
  );

  const activeOrder = useMemo(() => service.orders.find(v => v.orderType === activeOrderTab?.name), [
    service,
    activeOrderTab
  ]);

  const paymentReminderInvoices = useMemo(
    () => getPastDueUnpaidInvoices(activeOrder?.feeSourceInvoices?.invoices || []),
    [activeOrder?.feeSourceInvoices?.invoices]
  );
  const hasPastDueUnpaidInvoices =
    isAdmin(role) && activeOrder?.status === 'Pending To Pay' && paymentReminderInvoices.length > 0;

  const isWorkflowSplitOpen = useMemo(
    () => showWorkflowPanel && activeOrderTab?.name === 'Lien Search' && Boolean(activeOrder),
    [showWorkflowPanel, activeOrderTab?.name, activeOrder]
  );

  const {
    versions: orderVersions,
    loadingVersions: loadingOrderVersions,
    fetchVersions: fetchOrderVersions,
    showModal: showRequestUpdateModal,
    submitting: submittingRequestUpdate,
    closeModal: closeRequestUpdateModal,
    onRequestUpdateClick,
    onRequestUpdateSubmit
  } = useRequestUpdate({
    activeOrder,
    activeOrderTabKey: activeOrderTab?.key,
    service,
    onServiceUpdate,
    setAlertModalMeta
  });

  const lienSearchOrder = useMemo(
    () => service?.orders?.find(o => o.orderType === 'Lien Search'),
    [service?.orders]
  );
  const lienReportId = useMemo(() => {
    const r = lienSearchOrder?.reportId;
    if (r && typeof r === 'object' && r._id) return r._id;
    return r || null;
  }, [lienSearchOrder?.reportId]);

  const canEditLienReportMunicipality = [ADMIN_ROLE, PROCESSOR_ROLE].includes(role) && Boolean(lienReportId);

  const lienMuniSelectOptions = useMemo(() => {
    const base = municipalityOptions || [];
    const seen = new Set(base.map(o => o.value));
    const cur = (lienMuniEditOpen ? lienMuniDraft : service?.municipality) || '';
    if (cur && !seen.has(cur)) {
      return [{ label: cur, value: cur }, ...base];
    }
    return base;
  }, [municipalityOptions, lienMuniDraft, lienMuniEditOpen, service?.municipality]);

  useEffect(() => {
    setLienMuniEditOpen(false);
    setLienMuniDraft('');
    setLienMuniRowHover(false);
  }, [service?._id]);

  const beginLienMunicipalityEdit = e => {
    e.stopPropagation();
    void ensureRecordsLoaded();
    setLienMuniDraft(service?.municipality || '');
    setLienMuniEditOpen(true);
  };

  const cancelLienMunicipalityEdit = e => {
    e.stopPropagation();
    setLienMuniEditOpen(false);
    setLienMuniDraft('');
  };

  const saveLienMunicipalityEdit = async e => {
    e.stopPropagation();
    if (!lienReportId || lienMuniSaving) return;
    setLienMuniSaving(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.REPORT_WITH_ID(String(lienReportId)),
        method: 'PUT',
        requestBody: { municipality: lienMuniDraft || '', skipValidation: true }
      });
      if (error) {
        toast.error(error);
        return;
      }
      toast.success('Municipality updated');
      service.municipality = response?.municipality ?? lienMuniDraft ?? '';
      setLienMuniEditOpen(false);
      setLienMuniDraft('');
    } finally {
      setLienMuniSaving(false);
    }
  };

  const canClientEditBuyerNameAndInstruction =
    isClient(role) &&
    service?.orders?.length > 0 &&
    service.orders.every(o => {
      const clientStatus = getClientFacingOrderStatus(o.status, o.cancellationRequested);
      return clientStatus === 'New Order' || clientStatus === 'In Progress';
    });

  const isQualiaClientOrg = isQualiaOrganization(service?.organization);

  // inline edit handlers for service detail fields
  const beginInlineEdit = (field, currentValue, onBegin) => {
    setInlineEdit({ field, draft: currentValue ?? '', saving: false });
    onBegin?.();
  };

  const cancelInlineEdit = () => {
    setInlineEdit({ field: null, draft: '', saving: false });
  };

  const saveInlineEdit = async (serviceKey, draft) => {
    const trimmed = draft.trim();
    setInlineEdit(prev => ({ ...prev, saving: true }));
    try {
      await updateService({ [serviceKey]: trimmed === '' ? null : trimmed });
      setInlineEdit({ field: null, draft: '', saving: false });
    } finally {
      setInlineEdit(prev => ({ ...prev, saving: false }));
    }
  };

  const etaRequiredInitialValues = useMemo(() => {
    const toDateInput = value => (value ? moment(value).format('YYYY-MM-DD') : '');
    if (!activeOrder) return {};
    return {
      estimatedTurnAroundTime: toDateInput(activeOrder.estimatedTurnAroundTime),
      etaReason: activeOrder.etaReason || '',
      assignedSurveyor: activeOrder.assignedSurveyor || '',
      hardCost: activeOrder.hardCost ?? '',
      processingFee: activeOrder.processingFee ?? 95 // default processing fee for survey
    };
  }, [activeOrder]);

  const isSurveyOrderTab = activeOrderTab?.key === 'survey';
  const financialCloseoutPriceLabel = 'Hard Cost';

  const financialCloseoutInitialValues = useMemo(() => {
    if (!activeOrder) return { hardCost: '', invoiceAmount: null, dateDelivered: '' };
    return {
      hardCost: activeOrder.hardCost ?? '',
      invoiceAmount: getInvoiceAmountForOrder(activeOrder),
      dateDelivered: getFormattedDate(new Date())
    };
  }, [activeOrder]);

  const findIconForSpecificTab = currentTab => {
    if (currentTab.name === 'File Overview') return null;

    return service.orders.find(v => v.orderType === currentTab?.name) ? (
      <CheckCircleFill className="ml-2 text-success" />
    ) : (
      <PlusCircle className="ml-2 text-danger" />
    );
  };

  // check if the order has a valid ETA
  const orderHasEstimatedEta = order =>
    Boolean(
      order?.estimatedTurnAroundTime != null &&
      order.estimatedTurnAroundTime !== '' &&
      moment(order.estimatedTurnAroundTime).isValid()
    );

  const orderHasHardCost = order => {
    const v = order?.hardCost;
    return v != null && v !== '';
  };

  const runOrderStatusChangeFlow = async (newStatus, order) => {
    const etaNotRequiredStatuses = ['New Order', 'Due Diligence', 'Pending', 'Cancelled', 'Hold', 'Link Sent', 'Update'];

    if (!etaNotRequiredStatuses.includes(newStatus)) {
      if (!orderHasEstimatedEta(order)) {
        setPendingStatus(newStatus);
        setShowETARequiredModal(true);
        return;
      }
    }

    if (newStatus === 'Paid') {
      setPendingStatus('Paid');
      setPendingEtaPayload(null);
      setShowFinancialCloseoutModal(true);
      return;
    }

    if (newStatus === 'Pending To Pay') {
      if (!orderHasHardCost(order)) {
        setPendingStatus('Pending To Pay');
        setPendingEtaPayload(null);
        setShowFinancialCloseoutModal(true);
        return;
      }
      await updateOrder({ status: 'Pending To Pay' });
      return;
    }

    if (newStatus === 'Cancelled') {
      setOrderCancellationConfirmationModalMeta({
        dateOfCancellation: getFormattedDate(new Date()),
        reason: cancellationReasonOptions[0],
        priceFieldName: 'Hard Cost',
        price: order?.hardCost
      });
      return;
    }

    await updateOrder({ status: newStatus });
  };

  const onToggleOrderStatus = newStatus => {
    const order = activeOrder;
    if (!order || order.status === newStatus) return;

    if (newStatus === 'Revision' && order.orderType === 'Lien Search') {
      if (![ADMIN_ROLE, PROCESSOR_ROLE].includes(role)) {
        toast.error('Only Admins and Processors can request a revision.');
        return;
      }
      setRevisionRequestModalMeta({ orderId: order._id });
      return;
    }

    void (async () => {
      setLoadingMetaData(prevState => ({ ...prevState, status: true }));
      try {
        if (
          newStatus === 'Ready for QA' &&
          (order.orderType === 'Lien Search' || order.orderType === 'Estoppel')
        ) {
          const { error } = await makeRESTApiRequest({
            endpoint: ENDPOINTS.VALIDATE_REPORT,
            method: 'POST',
            requestBody: { orderId: order._id }
          });
          if (error) {
            toast.error('', {
              description: <div dangerouslySetInnerHTML={{ __html: toSafeApiErrorHtml(error) }} />
            });
            return;
          }
        }

        if (newStatus === 'Pending To Pay' && order.orderType === 'Lien Search') {
          setVerifyingLienSearchDriveForPendingToPay(true);
          try {
            const hasReport = await verifyLienSearchReportOnDrive();
            if (!hasReport) {
              showLienSearchReportMissingModal();
              return;
            }
          } finally {
            setVerifyingLienSearchDriveForPendingToPay(false);
          }
        }

        await runOrderStatusChangeFlow(newStatus, order);
      } catch (error) {
        console.error(error.message);
      } finally {
        setLoadingMetaData(prevState => ({ ...prevState, status: false }));
      }
    })();
  };

  const onEtaUpdate = value => {
    try {

      const normalizedEta = value == null || value === '' ? null : value;
      const existingEta = activeOrder?.estimatedTurnAroundTime || null;
      const hasEtaChanged =
        (existingEta ? moment(existingEta).format('YYYY-MM-DD') : null) !==
        (normalizedEta ? moment(normalizedEta).format('YYYY-MM-DD') : null);

      if (!hasEtaChanged) return;

      // show eta reason modal only when eta is updated
      if (existingEta) {
        setPendingEtaDate(normalizedEta);
        setShowETAReasonModal(true);
        return;
      }

      updateOrder({
        estimatedTurnAroundTime: normalizedEta
      });
    } catch (error) {
      console.error(error.message)
    }
  };

  const onRequestChangeClosingClick = () => {
    closingChange.open(service._id);
  };

  const onActivityClick = () => {
    setShowActivitySidebar(true);
  };

  const onRequestCancellationClick = order => {
    cancellation.open(order, service);
  };

  const onUndoCancellationClick = order => {
    setUndoCancellationOrder(order);
    setShowUndoCancellationModal(true);
  };

  const closeUndoCancellationConfirm = () => {
    setShowUndoCancellationModal(false);
  };

  const handleUndoCancellationModalExited = () => {
    setUndoCancellationOrder(null);
  };

  const onUndoCancellationConfirm = async () => {
    if (!undoCancellationOrder) return;
    await cancellation.undo(undoCancellationOrder);
    closeUndoCancellationConfirm();
  };

  /** STATE CHANGE FUNCTIONS */

  const onCancellationModalFieldChange = (field, value) => {
    setOrderCancellationConfirmationModalMeta(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /** API CALLS */
  const onNoteSubmit = async text => {
    const order = activeOrder;
    if (activeOrderTab.key === 'all') {
      await updateService({
        notes: [
          { text, userName: localStorage.getItem('user-name'), timestamp: new Date().toISOString() },
          ...(service['notes'] || [])
        ]
      });
    } else {
      await updateOrder({
        notes: [
          { text, userName: localStorage.getItem('user-name'), timestamp: new Date().toISOString() },
          ...(order['notes'] || [])
        ]
      });
    }

    setNoteText('');
  };

  const getNotesContext = () => {
    const currentUserName = localStorage.getItem('user-name');
    const notes = activeOrderTab.key === 'all' ? service['notes'] || [] : activeOrder?.notes || [];
    const updateNotes = updatedNotes =>
      activeOrderTab.key === 'all' ? updateService({ notes: updatedNotes }) : updateOrder({ notes: updatedNotes });
    return { currentUserName, notes, updateNotes };
  };

  const isNoteCreator = (note, currentUserName) =>
    Boolean(
      note?.userName &&
      currentUserName &&
      (note.userName || '').trim().toLowerCase() === (currentUserName || '').trim().toLowerCase()
    );

  const onNoteEdit = async (noteIndex, newText) => {
    const { currentUserName, notes, updateNotes } = getNotesContext();
    const note = notes[noteIndex];
    if (!note || !isNoteCreator(note, currentUserName)) return;
    const updated = [...notes];
    updated[noteIndex] = { ...note, text: newText, timestamp: new Date().toISOString() };
    await updateNotes(updated);
  };

  const onNoteDelete = async noteIndex => {
    const { currentUserName, notes, updateNotes } = getNotesContext();
    const note = notes[noteIndex];
    if (!note || !isNoteCreator(note, currentUserName)) return;
    await updateNotes(notes.filter((_, i) => i !== noteIndex));
  };

  const onCancelRequestOrderSubmit = async () => {
    await cancellation.submit();
  };

  // ETARequiredModal handlers
  const handleETARequiredModalSubmit = async formData => {
    const updatePayload = {
      status: pendingStatus,
      estimatedTurnAroundTime: formData.estimatedTurnAroundTime,
      etaReason: formData.etaReason
    };

    if (activeOrderTab?.name === 'Survey' && pendingStatus === 'Information Requested') {
      updatePayload.assignedSurveyor = formData.assignedSurveyor;
      if (formData.hardCost !== '' && formData.hardCost !== null && formData.hardCost !== undefined) {
        updatePayload.hardCost = Number(formData.hardCost);
      }
      if (formData.processingFee !== '' && formData.processingFee !== null && formData.processingFee !== undefined) {
        updatePayload.processingFee = Number(formData.processingFee);
      }
    }

    // Chain into Financial Closeout for Paid / Pending To Pay so the ETA values
    // we just collected are saved together with the financial fields.
    if (FINANCIAL_CLOSEOUT_STATUSES.includes(pendingStatus)) {
      if (activeOrder?.orderType === 'Lien Search' && pendingStatus === 'Pending To Pay') {
        setVerifyingLienSearchDriveForPendingToPay(true);
        let hasReport = false;
        try {
          hasReport = await verifyLienSearchReportOnDrive();
        } finally {
          setVerifyingLienSearchDriveForPendingToPay(false);
        }
        if (!hasReport) {
          showLienSearchReportMissingModal();
          return;
        }
      }
      if (pendingStatus === 'Pending To Pay' && orderHasHardCost(activeOrder)) {
        setIsSubmittingETARequired(true);
        try {
          await updateOrder(updatePayload);
          setShowETARequiredModal(false);
          setPendingStatus(null);
        } catch (error) {
          console.error('Error updating status:', error);
        } finally {
          setIsSubmittingETARequired(false);
        }
        return;
      }
      setPendingEtaPayload(updatePayload);
      setShowETARequiredModal(false);
      setShowFinancialCloseoutModal(true);
      return;
    }

    setIsSubmittingETARequired(true);
    try {
      await updateOrder(updatePayload);
      setShowETARequiredModal(false);
      setPendingStatus(null);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsSubmittingETARequired(false);
    }
  };

  const handleETARequiredModalCancel = () => {
    setShowETARequiredModal(false);
  };

  const handleETAReasonModalSubmit = async ({ etaReason, newEta }) => {
    setLoadingMetaData(prevState => ({ ...prevState, eta: true }));
    try {
      await updateOrder({
        estimatedTurnAroundTime: newEta,
        etaReason
      });
      setShowETAReasonModal(false);
      setPendingEtaDate(null);
    } catch (error) {
      console.error('Error updating ETA reason:', error);
    } finally {
      setLoadingMetaData(prevState => ({ ...prevState, eta: false }));
    }
  };

  const handleETAReasonModalCancel = () => {
    setShowETAReasonModal(false);
    setPendingEtaDate(null);
  };

  const handleFinancialCloseoutSubmit = async ({
    hardCost,
    invoices,
    allInvoicesFullyPaid,
    dateDelivered
  }) => {
    setIsSubmittingFinancialCloseout(true);
    try {
      if (activeOrder?._id && invoices?.length) {
        const { response, error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.ORDER_REPORT_INVOICES(activeOrder._id),
          method: 'PATCH',
          requestBody: { invoices }
        });
        if (error) {
          toast.error('', {
            description: <div dangerouslySetInnerHTML={{ __html: toSafeApiErrorHtml(error) }} />
          });
          return;
        }
        if (response?.createdPartialPaymentNotes) {
          financialCloseoutNotesCreatedRef.current = true;
        }
      }

      const statusToSend = pendingStatus || pendingEtaPayload?.status;
      const payload = {
        ...(pendingEtaPayload || {})
      };
      if (hardCost !== null) {
        payload.hardCost = hardCost;
      }
      if (statusToSend !== 'Pending To Pay' && dateDelivered) {
        payload.dateDelivered = getDateFromInputString(dateDelivered);
      }

      if (statusToSend === 'Paid' && (!invoices?.length || allInvoicesFullyPaid)) {
        payload.status = 'Paid';
      } else if (statusToSend && statusToSend !== 'Paid') {
        payload.status = statusToSend;
      }

      await updateOrder(payload);
      // Billing notes are created server-side without changing order status when
      // invoices still have a balance, so OrderNotesSection must be told to refetch.
      if (financialCloseoutNotesCreatedRef.current) {
        setOrderNotesRefreshKey(key => key + 1);
        financialCloseoutNotesCreatedRef.current = false;
      }
      setShowFinancialCloseoutModal(false);
      setPendingStatus(null);
      setPendingEtaPayload(null);
    } catch (error) {
      console.error('Error during financial closeout:', error);
    } finally {
      setIsSubmittingFinancialCloseout(false);
    }
  };

  const handleFinancialCloseoutCancel = () => {
    financialCloseoutNotesCreatedRef.current = false;
    setShowFinancialCloseoutModal(false);
    setPendingStatus(null);
    setPendingEtaPayload(null);
  };

  const onOrderCancellationConfirm = async () => {
    setOrderCancellationConfirmationModalMeta(prevState => ({
      ...prevState,
      showProgress: true
    }));

    const { dateOfCancellation, reason, price } = orderCancellationConfirmationModalMeta;
    await updateOrder({
      status: 'Cancelled',
      dateOfCancellation: getDateFromInputString(dateOfCancellation),
      reason,
      hardCost: price
    });
  };

  const updateService = async data => {
    setUpdatingService(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.SERVICE_WITH_ID(service._id),
        method: 'PUT',
        requestBody: { ...data }
      });
      if (error) {
        toast.error(error);
        return;
      }
      onServiceUpdate && onServiceUpdate({ ...service, ...data });
      toast.success('Service updated successfully');
    } catch (e) {
      toast.error(e);
    } finally {
      setUpdatingService(false);
    }
  };

  const updateOrder = async data => {
    const index = service.orders.findIndex(o => o.orderType === activeOrderTab.name);
    if (index !== -1) {
      setUpdatingService(true);
      try {
        const requestBody = { ...data };
        const { response, error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.ORDER_WITH_ID(service.orders[index]._id),
          method: 'PUT',
          requestBody: requestBody
        });
        if (error) {
          toast.error('', {
            description: <div dangerouslySetInnerHTML={{ __html: toSafeApiErrorHtml(error) }} />
          });
          return false;
        }

        const { order: orderResponse, qualiaTaskError } = splitQualiaTaskError(response);
        const { revisionTask, ...updatedOrder } = orderResponse || {};
        const responseTasks = Array.isArray(updatedOrder.tasks)
          ? updatedOrder.tasks
          : service.orders[index].tasks || [];
        const tasks =
          revisionTask &&
          !responseTasks.some(task => String(task?._id || '') === String(revisionTask?._id || ''))
            ? [...responseTasks, revisionTask]
            : responseTasks;
        service.orders[index] = { ...service.orders[index], ...updatedOrder, tasks };
        onServiceUpdate && onServiceUpdate({ ...service });
        setUpdatingService(false);
        setAlertModalMeta(null);
        setOrderCancellationConfirmationModalMeta(null);
        setUploadedFiles([...uploadedFiles.filter(file => file.status === 'ERROR')]);

        if (data?.status === 'Pending To Pay') {
          await fetchOrderVersions(service.orders[index]._id);
        }

        toastOrderUpdateResult(qualiaTaskError, 'Order updated successfully');
        return true;
      } catch (e) {
        toast.error(e);
        return false;
      } finally {
        setUpdatingService(false);
      }
    }
  };

  const handleTogglePin = async order => {
    const orderId = order?._id;
    if (!orderId || pinningOrderId) return;

    setPinningOrderId(orderId);

    try {
      const { error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORDER_PIN(orderId),
        method: order.pinned ? 'DELETE' : 'POST'
      });

      if (error) {
        toast.error(error);
        return;
      }

      const orderLabel = `${service?.fileName || getFullAddress(service) || order?.accurateId || 'Order'} - ${order.orderType}`;
      toast.success(order.pinned ? `Unpinned: ${orderLabel}` : `Pinned: ${orderLabel}`);

      const index = service.orders.findIndex(o => o._id === orderId);
      if (index !== -1) {
        service.orders[index] = { ...service.orders[index], pinned: !order.pinned };
        onServiceUpdate?.({ ...service });
      }
    } finally {
      setPinningOrderId(null);
    }
  };

  const onUpdateFormOrder = data => {
    const serviceDataToUpdate = {};
    if (data?.service?.supportingDocuments) {
      serviceDataToUpdate.supportingDocuments = data.service.supportingDocuments;
    }
    setUpdatingService(true);
    const index = service.orders.findIndex(o => o.orderType === activeOrderTab.name);
    if (index != -1) {
      service.orders[index] = { ...service.orders[index], ...data };
    } else {
      service.orders = [...service.orders, data];
    }
    onServiceUpdate && onServiceUpdate({ ...service, ...serviceDataToUpdate });
    setUpdatingService(false);
  };

  const onOrderEditClick = orderObject => {
    setAddUpdateOrderMeta(orderObject);
  };

  const onSyncOrderHardcost = async orderId => {
    if (!orderId) {
      toast.error('Order ID is required to fetch QuickBooks expenses.');
      return;
    }

    setLoadingMetaData(prevState => ({ ...prevState, quickBooksExpenses: true }));
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: `${ENDPOINTS.QUICKBOOKS_SYNC_ORDER_HARDCOST}?orderId=${encodeURIComponent(orderId)}`,
        method: 'GET'
      });

      if (error) {
        toast.error(error);
        return;
      }

      const orderIndex = service.orders.findIndex(o => o._id === orderId);
      if (orderIndex === -1) {
        toast.error('Order not found in service state.');
        return;
      }

      service.orders[orderIndex] = { ...service.orders[orderIndex], ...response };
      onServiceUpdate && onServiceUpdate({ ...service });
      toast.success('QuickBooks hard cost synced.');
    } catch (e) {
      toast.error(e);
    } finally {
      setLoadingMetaData(prevState => ({ ...prevState, quickBooksExpenses: false }));
    }
  };

  const onOrderDeleteClick = order => {
    setDeleteOrderMeta({ toBeDeletedOrder: order });
  };

  const deleteOrder = async () => {
    setUpdatingService(true);
    const { toBeDeletedOrder } = deleteOrderMeta;
    setDeleteOrderMeta({ ...deleteOrderMeta, deletingOrder: true });
    try {
      const { error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORDER_WITH_ID(toBeDeletedOrder._id),
        method: 'DELETE'
      });
      if (error) {
        toast.error(error);
        return;
      }
      onServiceUpdate({ ...service, orders: service.orders.filter(s => s._id !== toBeDeletedOrder._id) });
      setDeleteOrderMeta(null);
      toast.success('Order deleted successfully');
    } catch (e) {
      toast.error(e);
    } finally {
      setUpdatingService(false);
      setDeleteOrderMeta(null);
    }
  };

  const onFileDeleteClick = async order => {
    setDeleteFileOrderMeta({ ...order, deletingFile: true });
    await updateOrder({ files: showFileViewer.files.filter(f => f._id !== order._id).map(f => f._id) });
    setShowFileViewer({ ...showFileViewer, files: showFileViewer.files.filter(f => f._id !== order._id) });
    setDeleteFileOrderMeta(null);
  };

  const handleOnClickFileViewer = () => {
    setShowFileViewer({ driveFolder: service.driveFolder });
  };

  const handleAddFile = async () => {
    if (uploadedFiles.some(f => f.status === 'ERROR')) {
      return toast.error('One or more files have failed uploading, please discard these files and try again!');
    }
    const activeOrder = activeOrder;
    const oldFiles = activeOrder ? activeOrder.files.map(f => f._id) : [];
    await updateOrder({
      files: [...oldFiles, ...uploadedFiles?.filter(file => file.status !== 'ERROR').map(f => f.id)]
    });
  };

  const onOrderPaymentReminder = async (email, invoiceIds = []) => {
    const orderId = paymentReminderModalMeta?.id;
    if (!orderId || !invoiceIds.length) return [];

    setPaymentReminderModalMeta({ ...paymentReminderModalMeta, progress: true });
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORDER_PAYMENT_REMINDER(orderId),
        method: 'POST',
        requestBody: { recipient: email, invoiceIds }
      });
      if (error) {
        toast.error(error);
        setPaymentReminderModalMeta({ id: orderId, progress: false });
        return invoiceIds.map(id => String(id));
      }

      const sent = Array.isArray(response?.sent) ? response.sent : [];
      const failed = Array.isArray(response?.failed) ? response.failed : [];
      const sentCount = sent.length;
      const totalCount = invoiceIds.length;
      const sentOnByInvoiceId = Object.fromEntries(
        sent.map(item => [
          String(item.invoiceId),
          item.paymentReminderSentOn || new Date().toISOString()
        ])
      );
      const failedIds = failed.map(item => String(item.invoiceId));

      if (sentCount > 0) {
        const index = service.orders.findIndex(o => o._id === orderId || o.orderType === activeOrderTab.name);
        if (index != -1) {
          const existingOrder = service.orders[index];
          const existingInvoices = existingOrder?.feeSourceInvoices?.invoices || [];
          const { sent: _sent, failed: _failed, ...orderPayload } = response;
          service.orders[index] = {
            ...existingOrder,
            ...orderPayload,
            feeSourceInvoices: {
              ...(existingOrder.feeSourceInvoices || {}),
              invoices: existingInvoices.map(row => {
                const sentOn = sentOnByInvoiceId[String(row._id)];
                return sentOn ? { ...row, paymentReminderSentOn: sentOn } : row;
              })
            }
          };
        }
        onServiceUpdate({ ...service });
      }

      if (sentCount === totalCount && failedIds.length === 0) {
        toast.success(
          sentCount === 1
            ? 'Payment reminder sent successfully'
            : `${sentCount} payment reminders sent successfully`
        );
        setPaymentReminderModalMeta(null);
      } else if (sentCount > 0) {
        toast.warning(`${sentCount} of ${totalCount} payment reminders sent`);
        setPaymentReminderModalMeta({ id: orderId, progress: false });
      } else {
        const firstError = failed[0]?.error;
        toast.error(firstError || 'Failed to send payment reminder');
        setPaymentReminderModalMeta({ id: orderId, progress: false });
      }

      return failedIds;
    } catch (e) {
      toast.error(e);
      setPaymentReminderModalMeta({ id: orderId, progress: false });
      return invoiceIds.map(id => String(id));
    }
  };

  const onHandleTaskSubmit = async data => {
    const toBeUpdatedTask = addUpdateTaskModalMeta;
    setAddUpdateTasksModalMeta({ ...addUpdateTaskModalMeta, loading: true });
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: toBeUpdatedTask?._id ? ENDPOINTS.TASKS_WITH_ID(toBeUpdatedTask._id) : ENDPOINTS.Create_TASKS,
        method: toBeUpdatedTask?._id ? 'PUT' : 'POST',
        requestBody: { ...data, order: service.orders.find(o => o.orderType === activeOrderTab.name)?._id }
      });
      if (error) {
        toast.error(error);
        return;
      }
      const order = service.orders.find(o => o.orderType === activeOrderTab.name);
      if (!order) {
        toast.error('Order not found for this service');
        return;
      }
      const index = order.tasks.findIndex(t => t._id === toBeUpdatedTask?._id);
      if (index != -1) {
        order.tasks[index] = response;
      } else {
        order.tasks = [...order.tasks, response];
      }
      onServiceUpdate({ ...service });
      toast.success(`Task ${toBeUpdatedTask?._id ? 'updated' : 'added'} successfully`);
    } catch (e) {
      toast.error(e);
    } finally {
      setAddUpdateTasksModalMeta(null);
    }
  };

  const onRevisionRequestSubmit = async revisionTask => {
    setRevisionRequestModalMeta(previous => (previous ? { ...previous, loading: true } : previous));
    try {
      const updated = await updateOrder({ status: 'Revision', revisionTask });
      if (updated) setRevisionRequestModalMeta(null);
    } finally {
      setRevisionRequestModalMeta(previous => (previous ? { ...previous, loading: false } : null));
    }
  };

  const deleteTask = async () => {
    setTaskDeleteModalMeta({ ...taskDeleteModalMeta, loading: true });
    try {
      const { error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.TASKS_WITH_ID(taskDeleteModalMeta._id),
        method: 'DELETE'
      });
      if (error) {
        toast.error(error);
        return;
      }
      const order = service.orders.find(o => o.orderType === activeOrderTab.name);
      if (!order) {
        toast.error('Order not found for this service');
        return;
      }
      order.tasks = order.tasks.filter(t => t._id !== taskDeleteModalMeta._id);
      onServiceUpdate({ ...service });
      toast.success(`Task deleted successfully`);
    } catch (e) {
      toast.error(e);
    } finally {
      setTaskDeleteModalMeta(null);
    }
  };

  const onTaskStatusUpdate = task => {
    const order = service.orders.find(o => o.orderType === activeOrderTab.name);
    if (!order) return;
    const index = order.tasks.findIndex(t => t._id === task?._id);
    if (index != -1) {
      order.tasks[index] = task;
    }
    onServiceUpdate({ ...service });
  };

  const loadDriveFiles = async folderId => {
    setDriveFilesLoading(true);
    const { response, error } = await makeRESTApiRequest({
      method: 'GET',
      endpoint: `${ENDPOINTS.GOOGLE_DRIVE_READ_FOLDER}?folderId=${folderId}`
    });

    setDriveFilesLoading(false);

    if (error) {
      toast.error('Failed to load drive files');
      return;
    }

    setDriveFiles(response);
  };

  /** Returns true only if a file named with prefix `Lien_Search_Report` exists anywhere under the service Drive folder. */
  const verifyLienSearchReportOnDrive = async () => {
    if (!service.driveFolder) return false;
    const { response, error } = await makeRESTApiRequest({
      method: 'GET',
      endpoint: `${ENDPOINTS.GOOGLE_DRIVE_READ_FOLDER}?folderId=${service.driveFolder}`
    });
    if (error) {
      toast.error('Failed to verify drive files');
      return false;
    }
    if (response?.success === false) {
      toast.error(response?.message || 'Failed to verify drive files');
      return false;
    }
    setDriveFiles(response);
    return driveFolderTreeHasFileStartingWith(response, LIEN_SEARCH_REPORT_DRIVE_PREFIX);
  };

  const showLienSearchReportMissingModal = () => {
    setAlertModalMeta({
      alertText: "Lien Search Report hasn't been created and saved in the drive.",
      dismissButtonText: 'Dismiss',
      hideContinueButton: true
    });
  };

  useEffect(() => {
    if (service.driveFolder) {
      loadDriveFiles(service.driveFolder);
    }
  }, [service.driveFolder]);

  useEffect(() => {
    let isFilePromiseResoved = uploadedFiles.every(
      uploadedFile => uploadedFile.status === 'UPLOADED' || uploadedFile.status === 'ERROR'
    );

    if (isFilePromiseResoved && uploadedFiles?.length) {
      handleAddFile();
    }
  }, [uploadedFiles]);

  const onTabSelect = key => {
    const activeTab = servicesKeyValue.find(k => k.key === key);
    setActiveOrderTab(activeTab);
    onOrderTabChange?.(key === 'all' ? null : activeTab?.name);

    if (key === 'all') return;

    const orderType = activeTab?.name;
    const existingOrder = service.orders?.find(o => o.orderType === orderType);

    if (!existingOrder) {
      setAddUpdateOrderMeta({});
    }
  };

  const handleOrganizationClick = () => {
    if (isStaff(currentUserInfo.role) && service?.organization?._id) {
      window.open(`/dashboard?organizationId=${service.organization._id}`, '_blank');
    }
  };

  const propertyAppraiserDriveFile = useMemo(() => findPropertyAppraiserFile(driveFiles), [driveFiles]);
  const opensPropertyAppraiserFile = isPropertyAppraiserFileCounty(service?.county);
  // Wait until driveFiles finish loading; only look like a link when a PA item actually exists.
  const countyHasPropertyAppraiserLink = !driveFilesLoading && Boolean(propertyAppraiserDriveFile);

  const openPropertyAppraiserDestination = async e => {
    e.stopPropagation();
    e.preventDefault();
    if (propertyAppraiserOpening || !countyHasPropertyAppraiserLink) return;

    setPropertyAppraiserOpening(true);
    try {
      if (opensPropertyAppraiserFile) {
        // Miami-Dade / Lee / Broward → Property Appraiser file (refresh folder on click)
        let file = propertyAppraiserDriveFile;
        if (service?.driveFolder) {
          const { response, error } = await makeRESTApiRequest({
            method: 'GET',
            endpoint: `${ENDPOINTS.GOOGLE_DRIVE_READ_FOLDER}?folderId=${service.driveFolder}`
          });
          if (!error && response) {
            setDriveFiles(response);
            file = findPropertyAppraiserFile(response);
          }
        }

        const url = getDriveFileViewUrl(file?.id);
        if (!url) {
          toast.error('Property Appraiser file not found');
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      // Other counties → existing PA Drive link only when present (no speculative fetch/toast)
      const url = getDriveFileViewUrl(propertyAppraiserDriveFile?.id);
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setPropertyAppraiserOpening(false);
    }
  };

  // Copy to clipboard handler
  const handleCopyToClipboard = async (detail, e) => {
    // dont copy if clicking on see more button
    if (e.target.classList.contains('see-more-button') || e.target.closest('.see-more-button')) {
      return;
    }

    // dont copy if clicking on tooltip
    if (e.target.closest('[role="tooltip"]') || e.target.getAttribute('data-toggle') === 'tooltip') {
      return;
    }

    // dont copy if legal description or special instructions are empty
    if (detail.title === 'Legal Description' && !service['legalDescription']) {
      return;
    }
    if (detail.title === 'Modification/Special Instructions' && !service['modificationAndSpecialInstructions']) {
      return;
    }

    e.stopPropagation();

    // get text
    const textToCopy =
      e.currentTarget.textContent
        ?.trim()
        ?.replace('See more', '')
        ?.replace('See less', '') || '';

    if (!textToCopy || textToCopy === 'N/A') {
      toast.info('Nothing to copy');
      return;
    }

    copyToClipboard(textToCopy);
    toast.success(`${detail.title} copied to clipboard!`);
  };

  // copy folio numbers
  const handleCopyCleanedFolioNumbers = () => {
    const cleanedFolioNumbers = service['cleanedFolioNumbers'] || [];

    if (cleanedFolioNumbers.length === 0) {
      toast.info('Nothing to copy');
      return;
    }

    const textToCopy = cleanedFolioNumbers.join(', ');

    copyToClipboard(textToCopy);
    toast.success(`Clean Folio Numbers copied to clipboard!`);
  };

  return (
    <div className="font-sans" style={{ backgroundColor: '#fafafa' }}>
      {isAdmin(role) && (
        <PaymentReminderModal
          show={paymentReminderModalMeta}
          onHide={() => setPaymentReminderModalMeta(null)}
          onSubmit={onOrderPaymentReminder}
          showProgress={paymentReminderModalMeta?.progress}
          invoices={paymentReminderInvoices}
          orderTypeText={activeOrderTab?.name?.toLowerCase()}
          initialRecipientEmail={
            service?.isIndividualService && service?.individual?.email
              ? service.individual.email
              : service?.requestingPersonEmail || service?.organization?.email || ''
          }
        />
      )}
      <AlertModal
        show={deleteOrderMeta !== null}
        onHide={() => setDeleteOrderMeta(null)}
        onDismissClick={() => setDeleteOrderMeta(null)}
        onContinueClick={deleteOrder}
        showProgress={deleteOrderMeta?.deletingOrder}
        alertText={`Are you sure you want to delete this order ? This action cannot be undone!`}
        progressText="Deleting order..."
      />
      <AlertModal
        show={taskDeleteModalMeta !== null}
        onHide={() => setTaskDeleteModalMeta(null)}
        onDismissClick={() => setTaskDeleteModalMeta(null)}
        onContinueClick={deleteTask}
        showProgress={taskDeleteModalMeta?.loading}
        alertText={`Are you sure you want to delete this task ? This action cannot be undone!`}
        progressText="Deleting task..."
      />
      <AlertModal
        show={deleteFileOrderMeta !== null}
        onHide={() => setDeleteFileOrderMeta(null)}
        onDismissClick={() => setDeleteFileOrderMeta(null)}
        onContinueClick={() => onFileDeleteClick(deleteFileOrderMeta)}
        showProgress={deleteFileOrderMeta?.deletingFile}
        alertText={`Are you sure you want to delete this File? This action cannot be undone!`}
        progressText="Deleting File..."
      />
      <DriveFileReader
        show={showFileViewer}
        onHide={() => setShowFileViewer(null)}
        driveFolder={showFileViewer?.driveFolder}
        driveFiles={driveFiles}
        driveFilesLoading={driveFilesLoading}
        handleRefresh={() => loadDriveFiles(service.driveFolder)}
      />
      <AddEditTaskModal
        show={addUpdateTaskModalMeta !== null}
        onHide={() => setAddUpdateTasksModalMeta(null)}
        onSubmit={onHandleTaskSubmit}
        showProgress={addUpdateTaskModalMeta?.loading}
        edit={addUpdateTaskModalMeta}
      />
      <RevisionRequestModal
        show={revisionRequestModalMeta !== null}
        order={activeOrder}
        service={service}
        currentUser={currentUserInfo}
        submitting={revisionRequestModalMeta?.loading}
        onHide={() => setRevisionRequestModalMeta(null)}
        onSubmit={onRevisionRequestSubmit}
      />
      <div
        className={`px-2 py-2${isWorkflowSplitOpen ? ' d-flex align-items-start' : ''}`}
        style={{ minWidth: 0, gap: isWorkflowSplitOpen ? '0.5rem' : undefined }}
      >
        <div
          style={{
            width: isWorkflowSplitOpen ? '60%' : '100%',
            minWidth: 0,
            flexShrink: isWorkflowSplitOpen ? 0 : undefined
          }}
        >
        <div className="px-2 px-lg-4 ">
          {serviceId && (
            <div className="d-flex align-items-center pt-4" style={{ gap: '1rem' }}>
              <Button
                size="sm"
                variant="btn btn-outline-light border-0 text-muted"
                onClick={() => history.push('/services/pipeline')}
              >
                <ArrowLeft className="icon-lg" />
              </Button>
              <div>
                <h1 className="xxxlargeFont font-weight-bold mb-0">Service Details</h1>
                {service.accurateId && <p className="midFont text-muted mb-0">Order #{service.accurateId}</p>}
              </div>
            </div>
          )}

          <div className="mb-2  py-3">
            <div className="pt-0 py-1">
              <div className="d-flex flex-column flex-md-row flex-wrap align-items-start align-items-md-center justify-content-between mb-4" style={{ gap: '0.5rem' }}>
                <div className="text-info mr-1 d-flex align-items-center flex-wrap">
                  <div>
                    <div className="d-flex align-items-center flex-wrap">
                      <div className="d-flex align-items-center justify-content-center rounded text-muted">
                        <MapPin className="icon-lg text-muted mr-1 flex-shrink-0" />
                        <div
                          className="ml-1 mr-2 font-weight-semibold text-dark xxxlargeFont"
                          onClick={() => {
                            copyToClipboard(getFullAddress(service));
                            toast.success('Address copied to clipboard!');
                          }}
                          style={{
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.opacity = '0.7';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.opacity = '1';
                          }}
                        >
                          {getFullAddress(service)}
                        </div>
                      </div>
                      {isQualiaClientOrg && (
                        <div
                          className="d-flex align-items-center rounded-lg bg-dark ml-1 mr-2"
                          style={{ padding: '6px 10px' }}
                        >
                          <img
                            src="/qualia-logo.png"
                            alt="Qualia"
                            height={22}
                            style={{ width: 'auto', objectFit: 'contain', display: 'block' }}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      )}
                    </div>
                    {!isClient(role) && (
                      <div
                        className="ml-1 mr-2 text-muted midFont d-flex flex-wrap align-items-center"
                        onClick={() => {
                          copyToClipboard(service.accurateId);
                          toast.success('Service ID copied to clipboard!');
                        }}
                        style={{
                          cursor: 'pointer',
                          transition: 'opacity 0.2s',
                          height: 28,
                        }}
                      >
                        <span>
                          {`File No. ${service.accurateId}  |  County: `}
                          {countyHasPropertyAppraiserLink ? (
                            <span
                              role="link"
                              tabIndex={propertyAppraiserOpening ? -1 : 0}
                              className="text-info d-inline-flex align-items-center"
                              title={
                                propertyAppraiserOpening
                                  ? 'Opening Property Appraiser…'
                                  : opensPropertyAppraiserFile
                                    ? 'Open Property Appraiser file'
                                    : 'Open Property Appraiser link'
                              }
                              aria-busy={propertyAppraiserOpening}
                              style={{
                                cursor: propertyAppraiserOpening ? 'wait' : 'pointer',
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                                opacity: propertyAppraiserOpening ? 0.7 : 1,
                                gap: 4
                              }}
                              onClick={openPropertyAppraiserDestination}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  openPropertyAppraiserDestination(e);
                                }
                              }}
                            >
                              {service.county || 'N/A'}
                              {propertyAppraiserOpening && (
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-label="Opening Property Appraiser"
                                  style={{ width: 10, height: 10, borderWidth: 2 }}
                                />
                              )}
                            </span>
                          ) : (
                            service.county || 'N/A'
                          )}
                          {` | Property Type: ${service.propertyType?.toUpperCase() || 'N/A'} | Municipality:`}
                          &nbsp;
                        </span>
                        <span
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{ cursor: 'default' }}
                          onClick={e => e.stopPropagation()}
                          onMouseEnter={() => setLienMuniRowHover(true)}
                          onMouseLeave={() => setLienMuniRowHover(false)}
                        >
                          {!lienMuniEditOpen ? (
                            <>
                              <span>{service?.municipality || 'N/A'}</span>
                              {canEditLienReportMunicipality && lienMuniRowHover && (
                                <button
                                  type="button"
                                  className="btn btn-link p-0 ml-1 d-inline-flex align-items-center text-muted"
                                  style={{ lineHeight: 1 }}
                                  title="Edit municipality"
                                  onClick={beginLienMunicipalityEdit}
                                  aria-label="Edit municipality"
                                >
                                  <Pencil size={14} className="flex-shrink-0" />
                                </button>
                              )}
                            </>
                          ) : (
                            <span className="d-inline-flex align-items-center justify-content-center flex-wrap" style={{ gap: '0.35rem' }}>
                              <div
                                className="d-inline-block"
                                style={{ minWidth: '10rem', maxWidth: '14rem', width: '16rem' }}
                                onClick={e => e.stopPropagation()}
                              >
                                <AdvancedSelect
                                  items={lienMuniSelectOptions}
                                  selectedItems={lienMuniSelectOptions.find(option => option.value === lienMuniDraft)}
                                  onChange={v => {
                                    const value = v?.value ?? '';
                                    setLienMuniDraft(value || '');
                                  }}
                                  placeholder="Select Municipality"
                                  closeMenuOnSelect={true}
                                  isMulti={false}
                                  creatable={false}
                                  searchable={false}
                                  isClearable={true}
                                  originalOptions={lienMuniSelectOptions}
                                  disabled={lienMuniSaving}
                                  height={28}
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-link p-0 d-inline-flex align-items-center text-success"
                                disabled={lienMuniSaving}
                                title="Save"
                                onClick={saveLienMunicipalityEdit}
                                aria-label="Save municipality"
                              >
                                <Check size={18} className="flex-shrink-0" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-link p-0 d-inline-flex align-items-center text-danger"
                                disabled={lienMuniSaving}
                                title="Cancel"
                                onClick={cancelLienMunicipalityEdit}
                                aria-label="Cancel municipality edit"
                              >
                                <X size={18} className="flex-shrink-0" />
                              </button>
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-end ml-auto">
                  {!isClient(role) && (
                    <>
                      {activeOrderTab?.name === 'Lien Search' && (
                        <Button
                          variant={showWorkflowPanel ? 'success' : 'outline-success'}
                          className="d-flex align-items-center ml-1"
                          onClick={() => setShowWorkflowPanel(prev => !prev)}
                        >
                          <Play size={14} className="mr-1" />
                          Processing Workflow
                        </Button>
                      )}
                      <Button
                        variant="outline-dark"
                        className="d-flex align-items-center ml-1"
                        onClick={onServiceEditClick}
                      >
                        <Pen size={14} className="mr-1" />
                        Edit
                      </Button>
                      {isAdmin(role) && (
                        <Button
                          variant="outline-danger"
                          onClick={onServiceDeleteClick}
                          className="ml-1 d-inline-flex align-items-center shadow-sm"
                        >
                          <Trash size={13} className="mr-1" />
                          Delete
                        </Button>
                      )}
                      <Button
                        variant="outline-dark"
                        className="d-flex align-items-center ml-1"
                        onClick={() => handleOnClickFileViewer(activeOrder)}
                      >
                        <Files size={14} className="mr-1" />
                        Files
                      </Button>

                      {service['driveFolder'] && (
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={`https://drive.google.com/drive/folders/${service['driveFolder']}`}
                          className="ml-1"
                        >
                          <Button variant="outline-dark" className="d-flex align-items-center">
                            <ArrowUpRightCircle size={14} className="mr-1" />
                            Open Drive
                          </Button>
                        </a>
                      )}

                      {canViewActivity && (
                        <Button
                          variant="outline-dark"
                          className="d-flex align-items-center ml-1"
                          onClick={onActivityClick}
                          disabled={updatingService}
                        >
                          <MenuButton size={14} className="mr-1" />
                          Activity
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {/* Service Details Grid */}
              <div>
                <div className="row" style={{ rowGap: '1rem' }}>
                  {[
                    {
                      icon: <Calendar className="icon-sm" />,
                      title: 'Closing Date',
                      value: service['closingDate'] ? moment(service['closingDate']).format('MMMM Do, YYYY') : 'N/A',
                      additionalComponent: (
                        isClient(role) && !service['newClosingDate'] ? (
                          <Button
                            size="sm"
                            onClick={onRequestChangeClosingClick}
                            disabled={updatingService}
                            style={{ borderRadius: '8px', padding: '2px 6px' }}
                            className="mx-2 shadow-none bg-transparent border hover-muted d-flex align-items-center"
                          >
                            <Pencil size={14} className="mr-1" />
                            Change Date
                          </Button>
                        ) : undefined
                      )
                    },
                    {
                      icon: <FileText className="icon-sm" />,
                      title: 'File Name/Number',
                      value: service['fileName'] || 'N/A'
                    },
                    {
                      icon: <FileText className="icon-sm" />,
                      title: 'Folio Numbers',
                      warningTooltipText:
                        !isClient(role) && service?.hasCrossOrgFolioDuplicates
                          ? `Folio No. found in another order.`
                          : undefined,
                      value: service['folioNumbers'] && service['folioNumbers'].join(', '),
                      additionalComponent: (
                        <button
                          title="Copy Cleaned Folio Number"
                          className="p-0 m-0 border-0 bg-transparent"
                          onMouseEnter={e => {
                            e.currentTarget.style.opacity = '0.8';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.opacity = '1';
                          }}
                          style={{
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                          onClick={() => handleCopyCleanedFolioNumbers()}
                        >
                          <RiBrushAiFill size={16} className="ml-2" style={{ color: '#f59e0b' }} />
                        </button>
                      )
                    },
                    {
                      icon: <FileText className="icon-sm" />,
                      title: 'Legal Description',
                      value: (
                        <div
                          className={` d-flex align-items-end ${isLegalDescriptionExpanded ? 'flex-column justify-content-end' : ''
                            }`}
                        >
                          <p
                            ref={legalDescriptionRef}
                            className={`mb-0 largeFont ${!isLegalDescriptionExpanded ? 'text-clamp-1' : 'mb-1'}`}
                            style={{ whiteSpace: 'pre-line' }}
                          >
                            {service['legalDescription'] ? (
                              service['legalDescription']
                            ) : (
                              <span className="text-muted largeFont">No legal description provided!</span>
                            )}
                          </p>
                          {service['legalDescription'] && (isLegalDescriptionClamped || isLegalDescriptionExpanded) && (
                            <button
                              className="see-more-button"
                              onClick={e => {
                                e.stopPropagation();
                                setIsLegalDescriptionExpanded(!isLegalDescriptionExpanded);
                              }}
                            >
                              {isLegalDescriptionExpanded ? 'See less' : 'See more'}
                            </button>
                          )}
                        </div>
                      )
                    },
                    // Buyer Details
                    ...((service['buyerName'] || service['buyerPhone'] || service['buyerEmail'])
                      ? [
                        {
                          icon: <User className="icon-sm" />,
                          title: 'Buyer',
                          isInlineEditable: canClientEditBuyerNameAndInstruction,
                          serviceKey: 'buyerName',
                          value: (
                            <div className="font-weight-medium largeFont">
                              <div className="text-dark">{service['buyerName'] || 'N/A'}</div>

                              {service['buyerPhone'] && (
                                <div className="text-muted mr-2 midFont">
                                  {/* <Phone size={12} className="mr-1" /> */}
                                  {service['buyerPhone']}
                                </div>
                              )}
                              {service['buyerEmail'] && (
                                <div className="text-muted midFont">
                                  {/* <Envelope size={12} className="mr-1" /> */}
                                  {service['buyerEmail']}
                                </div>
                              )}
                            </div>
                          )
                        }
                      ]
                      : []),
                    // Seller Details
                    ...(service['sellerName'] || service['sellerPhone'] || service['sellerEmail']
                      ? [
                        {
                          icon: <User className="icon-sm" />,
                          title: 'Seller',
                          value: (
                            <div className="font-weight-medium largeFont">
                              <div className="text-dark">{service['sellerName'] || 'N/A'}</div>
                              {service['sellerPhone'] && (
                                <div className="text-muted mr-2 midFont">
                                  {/* <Phone size={12} className="mr-1" /> */}
                                  {service['sellerPhone']}
                                </div>
                              )}
                              {service['sellerEmail'] && (
                                <div className="text-muted midFont">
                                  {/* <Envelope size={12} className="mr-1" /> */}
                                  {service['sellerEmail']}
                                </div>
                              )}
                            </div>
                          )
                        }
                      ]
                      : []),
                    //Organization and Individual Details
                    ...(!isClient(role)
                      ? service?.isIndividualService
                        ? [
                          {
                            icon: <User className="icon-sm" />,
                            title: 'Individual Details',
                            value: (
                              <div className="font-weight-medium largeFont">
                                <div className="text-dark">{service?.individual?.name || 'N/A'}</div>
                                {service?.individual?.email && (
                                  <div className="text-muted  mr-2">{service?.individual?.email || 'N/A'}</div>
                                )}
                                {service?.individual?.phoneNumber && (
                                  <div className="text-muted ">{service?.individual?.phoneNumber || 'N/A'}</div>
                                )}
                              </div>
                            )
                          }
                        ]
                        : [
                          {
                            icon: <Building2 className="icon-sm" />,
                            title: 'Organization',
                            value: service?.organization?.name || '',
                            tooltipText: service?.organization
                              ? getOrganizationTooltipText(service?.organization)
                              : getPublicServiceTooltipText(service),
                            isNotFoundComponentAvailable: !service?.organization?.name,
                            NotFoundComponent: service?.organization?.name ? null : (
                              <AddOrganization
                                newOrg={newOrg}
                                setNewOrg={setNewOrg}
                                id={service?._id}
                                orgs={orgs}
                                onServiceUpdate={onServiceUpdate}
                                setItemActionMeta={setItemActionMeta}
                              />
                            ),
                            onClick: handleOrganizationClick
                          },
                          ...(service?.requestedBy?.name || service?.requestedBy?.email
                            ? [
                              {
                                icon: <User className="icon-sm" />,
                                title: 'Requested By',
                                value: (
                                  <div className="font-weight-medium largeFont">
                                    <div className="text-dark">{service.requestedBy.name || 'N/A'}</div>
                                    {service.requestedBy.email && (
                                      <div className="text-muted midFont">{service.requestedBy.email}</div>
                                    )}
                                  </div>
                                )
                              }
                            ]
                            : [])
                        ]
                      : []),
                    {
                      icon: <FileText className="icon-sm" />,
                      title: 'Special Instructions',
                      isInlineEditable: canClientEditBuyerNameAndInstruction,
                      serviceKey: 'modificationAndSpecialInstructions',
                      multiline: true,
                      onBeginEdit: () => setIsSpecialInstructionsExpanded(true),
                      value: (
                        <div
                          className={` d-flex align-items-end ${isSpecialInstructionsExpanded ? 'flex-column justify-content-end' : ''
                            }`}
                        >
                          <p
                            ref={specialInstructionsRef}
                            className={`mb-0 font-weight-medium largeFont ${!isSpecialInstructionsExpanded ? 'text-clamp-1' : ''
                              }`}
                            style={{ whiteSpace: 'pre-line' }}
                          >
                            {service['modificationAndSpecialInstructions'] ? (
                              service['modificationAndSpecialInstructions']
                            ) : (
                              <span className="text-muted largeFont">No special instructions provided!</span>
                            )}
                          </p>
                          {service['modificationAndSpecialInstructions'] &&
                            inlineEdit.field !== 'specialInstructions' &&
                            (isSpecialInstructionsClamped || isSpecialInstructionsExpanded) && (
                              <button
                                className="see-more-button"
                                onClick={e => {
                                  e.stopPropagation();
                                  setIsSpecialInstructionsExpanded(!isSpecialInstructionsExpanded);
                                }}
                              >
                                {isSpecialInstructionsExpanded ? 'See less' : 'See more'}
                              </button>
                            )}
                        </div>
                      )
                    }
                  ].map((detail, index) => {
                    const isEditing = detail.serviceKey != null && inlineEdit.field === detail.serviceKey;
                    const isSaving = isEditing && inlineEdit.saving;
                    const canEdit = detail.isInlineEditable;

                    return (
                      <div key={index} className="col-12 col-sm-6 col-md-6 col-lg-3">
                        <div>
                          <div className="d-flex align-items-center mb-2 text-muted midFont">
                            <div className="mr-2 d-flex align-items-center flex-shrink-0">{detail.icon}</div>
                            <span>{detail.title}</span>
                            {detail.tooltipText && (
                              <OverlayTrigger
                                placement="bottom"
                                delay={50}
                                overlay={
                                  <Tooltip style={{ minWidth: 400 }} id={`tooltip-${detail.title}`}>
                                    {detail.tooltipText || ''}
                                  </Tooltip>
                                }
                              >
                                <InfoCircleFill size={12} className="ml-2 text-muted" />
                              </OverlayTrigger>
                            )}
                            {detail.warningTooltipText && (
                              <OverlayTrigger
                                placement="bottom"
                                delay={50}
                                overlay={
                                  <Tooltip style={{ minWidth: 400 }} id={`tooltip-${detail.title}`}>
                                    {detail.warningTooltipText || ''}
                                  </Tooltip>
                                }
                              >
                                <AlertTriangle size={13} className="ml-1" style={{ color: '#f59e0b' }} />
                              </OverlayTrigger>
                            )}
                            {canEdit && (
                              <button
                                type="button"
                                className="btn btn-link p-0 ml-1 d-inline-flex align-items-center justify-content-center text-muted"
                                disabled={isSaving || isEditing}
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  beginInlineEdit(detail.serviceKey, service[detail.serviceKey], detail.onBeginEdit);
                                }}
                              >
                                <Pencil size={14} className="flex-shrink-0" />
                              </button>
                            )}
                          </div>
                          <div className="d-flex align-items-center">
                            {detail.isNotFoundComponentAvailable ? (
                              <div className="d-flex align-items-center w-100">
                                {detail.NotFoundComponent}
                              </div>
                            ) : canEdit && isEditing ? (
                              <div className="d-flex align-items-center w-100" style={{ minHeight: 28 }}>
                                <Form.Control
                                  as={detail.multiline ? 'textarea' : 'input'}
                                  size="sm"
                                  type="text"
                                  className="mr-2"
                                  style={detail.multiline ? { width: '100%', minHeight: 80 } : { flex: '1 1 auto', minWidth: 200 }}
                                  value={inlineEdit.draft}
                                  onChange={e => setInlineEdit(prev => ({ ...prev, draft: e.target.value }))}
                                  disabled={isSaving}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' && !detail.multiline) void saveInlineEdit(detail.serviceKey, inlineEdit.draft);
                                    if (e.key === 'Escape') cancelInlineEdit();
                                  }}
                                  aria-label={detail.title}
                                />
                                <InlineEditSaveCancel
                                  saving={isSaving}
                                  onSave={() => saveInlineEdit(detail.serviceKey, inlineEdit.draft)}
                                  onCancel={cancelInlineEdit}
                                  saveAriaLabel={`Save ${detail.title}`}
                                />
                              </div>
                            ) : (
                              <>
                                <div
                                  style={{
                                    cursor: 'pointer',
                                    transition: 'opacity 0.2s'
                                  }}
                                  onClick={e => handleCopyToClipboard(detail, e)}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.opacity = '0.7';
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.opacity = '1';
                                  }}
                                  className="font-weight-medium largeFont "
                                  title={detail.onClick ? '' : `Click to copy ${detail.title}`}
                                >
                                  {detail.value || ''}
                                  {detail.onClick && (
                                    <SquareArrowOutUpRight
                                      className="ml-1 mt-1 cursor-pointer"
                                      size={10}
                                      onClick={detail.onClick}
                                      onMouseEnter={e => {
                                        e.currentTarget.style.opacity = '1';
                                      }}
                                      onMouseLeave={e => {
                                        e.currentTarget.style.opacity = '0.7';
                                      }}
                                    />
                                  )}
                                </div>
                                {detail.additionalComponent && detail.additionalComponent}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <ClientAttachmentsBar
            documents={service.supportingDocuments}
            onDocumentsChange={handleSupportingDocumentsChange}
            folderId={service.driveFolder}
            orderType={attachmentOrderType}
            serviceId={service._id}
            disabled={updatingService}
          />

          <div className="py-2  ">
            <div className="d-flex justify-content-between align-items-center pb-4">
              <OrdersTabPills
                tabs={servicesTabs}
                activeTabKey={activeOrderTab.key}
                onTabSelect={onTabSelect}
                justify={false}
                fill={false}
              />
              {activeOrder && !isSurveyOrderTab && (
                <div className="d-flex align-items-center" style={{ gap: '0.75rem' }}>
                  <OrderVersionPills
                    versions={orderVersions}
                    loading={loadingOrderVersions || submittingRequestUpdate}
                  />
                  {!(isClient(role) && activeOrder.orderType === 'Estoppel') && (
                    <Button
                      variant="outline-warning"
                      className="btn-request-update d-flex align-items-center font-weight-semibold"
                      onClick={onRequestUpdateClick}
                    >
                      <Plus size={14} className="mr-1" />
                      Request Update
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="">
              {activeOrderTab.key === 'all' && (
                <FileOverview
                  service={service}
                  disabled={updatingService}
                  onEditClick={onServiceEditClick}
                  onDeleteClick={onServiceDeleteClick}
                  onRequestChangeClosingClick={onRequestChangeClosingClick}
                  onClickOpenFileViewer={() => setShowFileViewer(true)}
                  onServiceUpdate={onServiceUpdate}
                />
              )}

              {activeOrderTab.key !== 'all' && activeOrder && activeOrderTab.key === 'lienSearch' ? (
                <div className="h-100">
                  <LienSearch
                    lienSearch={service.orders.find(v => v.orderType === 'Lien Search')}
                    disabled={updatingService || verifyingLienSearchDriveForPendingToPay}
                    onStatusToggle={onToggleOrderStatus}
                    onRequestCancellationClick={onRequestCancellationClick}
                    onUndoCancellationClick={onUndoCancellationClick}
                    onDeleteClick={onOrderDeleteClick}
                    onPaymentReminderClicked={id => setPaymentReminderModalMeta({ id })}
                    hasPastDueUnpaidInvoices={hasPastDueUnpaidInvoices}
                    service={service}
                    latestReport={activeOrder?.latestReport?.latestReportFile}
                    onEtaUpdate={onEtaUpdate}
                    onSyncOrderHardcost={onSyncOrderHardcost}
                    loadingMetaData={loadingMetaData}
                    onOrderFieldUpdate={updateOrder}
                    onTogglePin={handleTogglePin}
                    isPinning={pinningOrderId === activeOrder?._id}
                  />
                </div>
              ) : activeOrderTab.key !== 'all' && activeOrder && activeOrderTab.key === 'estoppel' ? (
                <div className="h-100">
                  <Estoppel
                    estoppel={service.orders.find(v => v.orderType === 'Estoppel')}
                    disabled={updatingService}
                    onStatusToggle={onToggleOrderStatus}
                    onRequestCancellationClick={onRequestCancellationClick}
                    onUndoCancellationClick={onUndoCancellationClick}
                    onEditClick={onOrderEditClick}
                    onDeleteClick={onOrderDeleteClick}
                    onPaymentReminderClicked={id => setPaymentReminderModalMeta({ id })}
                    hasPastDueUnpaidInvoices={hasPastDueUnpaidInvoices}
                    service={service}
                    onEtaUpdate={onEtaUpdate}
                    onSyncOrderHardcost={onSyncOrderHardcost}
                    loadingMetaData={loadingMetaData}
                    onOrderFieldUpdate={updateOrder}
                    onTogglePin={handleTogglePin}
                    isPinning={pinningOrderId === activeOrder?._id}
                  />
                </div>
              ) : activeOrderTab.key !== 'all' && activeOrder && activeOrderTab.key === 'survey' ? (
                <div className="h-100">
                  <Survey
                    survey={service.orders.find(v => v.orderType === 'Survey')}
                    disabled={updatingService}
                    onStatusToggle={onToggleOrderStatus}
                    onRequestCancellationClick={onRequestCancellationClick}
                    onUndoCancellationClick={onUndoCancellationClick}
                    onDeleteClick={onOrderDeleteClick}
                    onPaymentReminderClicked={id => setPaymentReminderModalMeta({ id })}
                    hasPastDueUnpaidInvoices={hasPastDueUnpaidInvoices}
                    service={service}
                    onEtaUpdate={onEtaUpdate}
                    onSyncOrderHardcost={onSyncOrderHardcost}
                    loadingMetaData={loadingMetaData}
                    onOrderFieldUpdate={updateOrder}
                    onTogglePin={handleTogglePin}
                    isPinning={pinningOrderId === activeOrder?._id}
                  />
                </div>
              ) : (
                <div className="d-flex" style={{ gap: '1rem' }}>
                  <div className={`h-100 ${activeOrder ? 'w-50' : 'w-100'}`}>
                    <div className="d-flex" style={{ gap: '1rem' }}>
                      {activeOrderTab.key !== 'all' && !activeOrder && (
                        <div className="border rounded p-5 w-100">
                          <div className="d-flex flex-column align-items-center justify-content-center h-100">
                            <div className="text-center">
                              <p className="mb-1">An order for this service has not been placed yet!</p>
                              <Button
                                variant="success"
                                onClick={() => setAddUpdateOrderMeta({})}
                                className="d-inline-flex align-items-center shadow-sm"
                              >
                                <PlusCircle size={13} className="mr-1" />
                                Place a new order
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Tasks and Notes section*/}
              {!isClient(role) && activeOrder && (
                <div className="d-flex w-100 h-100 mt-3" style={{ gap: '1rem' }}>
                  <>
                    <div className="w-50 h-100">
                      <Tasks
                        tasks={activeOrder?.tasks}
                        onAddClick={() => setAddUpdateTasksModalMeta({})}
                        onTaskDelete={task => setTaskDeleteModalMeta(task)}
                        onTaskStatusUpdate={task => onTaskStatusUpdate(task)}
                        onEditClick={task => setAddUpdateTasksModalMeta(task)}
                        fromDetails
                        onShowFilterSidebarChange={onShowFilterSidebarChange}
                        onServiceSortOptionChange={onServiceSortOptionChange}
                      />
                    </div>
                    <div className="w-50 h-100">
                      <Notes
                        bordered={Boolean(activeOrder)}
                        notes={activeOrderTab.key === 'all' ? service['notes'] : activeOrder?.notes}
                        disabled={updatingService}
                        onNoteSubmit={onNoteSubmit}
                        text={noteText}
                        onTextChange={setNoteText}
                        onNoteEdit={onNoteEdit}
                        onNoteDelete={onNoteDelete}
                        currentUserName={localStorage.getItem('user-name')}
                      />
                    </div>
                  </>
                </div>
              )}

              {activeOrderTab.key !== 'all' && activeOrder && (
                <OrderNotesSection
                  orderId={activeOrder._id}
                  orderType={activeOrder.orderType}
                  status={activeOrder.status}
                  disabled={updatingService}
                  refreshKey={orderNotesRefreshKey}
                />
              )}

              {activeOrderTab.key !== 'all' && activeOrder && !isSurveyOrderTab && (
                <UpdateHistory
                  versions={orderVersions}
                  order={activeOrder}
                  orderType={activeOrderTab?.name}
                />
              )}

              {/* Drive file uploader */}
              {activeOrderTab.key !== 'all' && activeOrder && !isClient(role) && (
                <div className="card py-2 px-3 mt-3" style={{ height: '150px' }}>
                  <DriveFileUploader
                    variant="upload-area"
                    multiple={true}
                    filesToBeUploaded={uploadedFiles}
                    onUploadedFilesChange={setUploadedFiles}
                    onFileSave={f => {
                      setUploadedFiles([]);
                      toast.success('File uploaded successfully to drive');
                    }}
                    deletable={false}
                    folderId={service.driveFolder}
                    subFolderName={activeOrderTab.name}
                  />
                </div>
              )}

              {isClient(role) && (
                <>
                  <ClosingChangeRequestModal
                    show={closingChange.state.isOpen}
                    onHide={closingChange.close}
                    values={closingChange.state.values}
                    onValuesChange={closingChange.updateField}
                    onSubmit={closingChange.submit}
                    submittingChangeRequest={updatingService}
                  />
                  <CancelRequestModal
                    service={service}
                    orderType={activeOrderTab?.name}
                    show={cancellation.state.isOpen}
                    onHide={cancellation.close}
                    values={cancellation.state.order}
                    onValuesChange={cancellation.updateOrderField}
                    onSubmit={onCancelRequestOrderSubmit}
                    cancelRequestSubmitting={cancellation.state.submitting}
                  />
                </>
              )}
              <AlertModal
                show={alertModalMeta !== null}
                onHide={() => setAlertModalMeta(null)}
                onDismissClick={() => setAlertModalMeta(null)}
                onContinueClick={alertModalMeta?.onContinueClick}
                dismissButtonText={alertModalMeta?.dismissButtonText}
                continueButtonText={alertModalMeta?.continueButtonText}
                continueButtonVariant={alertModalMeta?.continueButtonVariant}
                hideContinueButton={Boolean(alertModalMeta?.hideContinueButton)}
                showProgress={alertModalMeta?.showProgress}
                title={alertModalMeta?.title}
                alertText={alertModalMeta?.alertText}
                progressText={alertModalMeta?.progressText}
              />
              <RequestUpdateModal
                show={showRequestUpdateModal}
                onHide={closeRequestUpdateModal}
                service={service}
                order={activeOrder}
                versions={orderVersions}
                submitting={submittingRequestUpdate}
                onSubmit={onRequestUpdateSubmit}
              />
              {undoCancellationOrder && (
                <UndoCancellationModal
                  show={showUndoCancellationModal}
                  orderType={undoCancellationOrder.orderType}
                  orderLabel={undoCancellationOrder.accurateId}
                  onExited={handleUndoCancellationModalExited}
                  onHide={() => {
                    if (cancellation.state.submitting || updatingService) return;
                    closeUndoCancellationConfirm();
                  }}
                  onSubmit={onUndoCancellationConfirm}
                  submitting={cancellation.state.submitting || updatingService}
                />
              )}
              {!isClient(role) && (
                <>
                  <OrderCancellationConfirmationModal
                    show={orderCancellationConfirmationModalMeta !== null}
                    dateOfCancellation={orderCancellationConfirmationModalMeta?.dateOfCancellation}
                    onDateOfCancellationChange={date => onCancellationModalFieldChange('dateOfCancellation', date)}
                    reason={orderCancellationConfirmationModalMeta?.reason}
                    onReasonChange={reason => onCancellationModalFieldChange('reason', reason)}
                    priceFieldName={orderCancellationConfirmationModalMeta?.priceFieldName}
                    price={orderCancellationConfirmationModalMeta?.price}
                    onPriceChange={price => onCancellationModalFieldChange('price', price)}
                    onHide={() => setOrderCancellationConfirmationModalMeta(null)}
                    onSubmit={onOrderCancellationConfirm}
                    showProgress={orderCancellationConfirmationModalMeta?.showProgress}
                  />
                </>
              )}

              {/* ETARequiredModal */}
              <ETARequiredModal
                show={showETARequiredModal}
                onHide={handleETARequiredModalCancel}
                showProgress={isSubmittingETARequired || verifyingLienSearchDriveForPendingToPay}
                onSubmit={handleETARequiredModalSubmit}
                orderType={activeOrderTab?.name}
                pendingStatus={pendingStatus}
                initialValues={etaRequiredInitialValues}
              />

              {/* ETAReasonModal */}
              <ETAReasonModal
                show={showETAReasonModal}
                onHide={handleETAReasonModalCancel}
                showProgress={loadingMetaData?.eta}
                onSubmit={handleETAReasonModalSubmit}
                initialValues={{ newEta: pendingEtaDate || '', etaReason: '' }}
                setLoadingMetaData={setLoadingMetaData}
              />

              <FinancialCloseoutModal
                show={showFinancialCloseoutModal}
                onHide={handleFinancialCloseoutCancel}
                showProgress={isSubmittingFinancialCloseout}
                onSubmit={handleFinancialCloseoutSubmit}
                pendingStatus={pendingStatus}
                orderId={activeOrder?._id}
                initialValues={financialCloseoutInitialValues}
                primaryPriceLabel={financialCloseoutPriceLabel}
              />

              <AddEditOrderSidebar
                orderType={activeOrderTab.name}
                show={addUpdateOrderMeta !== null}
                onHide={() => setAddUpdateOrderMeta(null)}
                editingOrder={addEditOrderEditsFromService}
                onOrderAddUpdate={onUpdateFormOrder}
                service={service}
              />
              <ActivitySidebar
                service={service}
                visible={showActivitySidebar}
                onClose={() => setShowActivitySidebar(false)}
              />
              {/* Modal to add new organization with admin */}
              <AddOrganizationForm
                name={itemActionMeta?.initialValue}
                adminName={service?.requestingPersonName}
                adminEmail={service?.requestingPersonEmail}
                setItemActionMeta={setItemActionMeta}
                orgs={orgs}
                setOrgs={setOrgs}
                setNewOrg={setNewOrg}
              />
            </div>
            <div className="tinyFont text-muted text-right p-2">
              <i>
                Entered by{' '}
                <b>
                  {service.modifiedBy?.name} ({service.modifiedBy?.email})
                </b>
                {(service.requestedBy?.name || service.requestedBy?.email) && (
                  <>
                    {' '}
                    on behalf of{' '}
                    <b>
                      {service.requestedBy.name}
                      {service.requestedBy.email ? ` (${service.requestedBy.email})` : ''}
                    </b>
                  </>
                )}
              </i>
            </div>
          </div>
        </div>
        </div>
        {isWorkflowSplitOpen && (
          <div
            style={{
              width: '40%',
              minWidth: 0,
              flexShrink: 0,
              alignSelf: 'flex-start',
              position: 'sticky',
              top: WORKFLOW_PANEL_STICKY_TOP,
              height: WORKFLOW_PANEL_HEIGHT,
              maxHeight: WORKFLOW_PANEL_HEIGHT,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <WorkflowModal
              show={showWorkflowPanel}
              onClose={() => setShowWorkflowPanel(false)}
              service={service}
              rowOrderId={activeOrder._id}
              handleServiceUpdate={onServiceUpdate}
              renderInline
              inlineHeaderStyle="sidebar"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceCard;
